import {
  feedWaveformBytes, markNoWaveform, peekWaveform, subscribeWaveform, waveformCacheKey,
  WAVEFORM_MAX_SOURCE_BYTES, type WaveformState,
} from '@/services/waveformService';

/** The body of `res` as bytes, or null when it is longer than `max` (the rest is cancelled unread). */
async function readCapped(res: Response, max: number): Promise<Uint8Array | null> {
  if (!res.body) {
    const bytes = new Uint8Array(await res.arrayBuffer());
    return bytes.length > max ? null : bytes;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.length;
    if (total > max) {
      void reader.cancel().catch(() => {});
      return null;
    }
    chunks.push(value);
  }
  const bytes = new Uint8Array(total);
  let at = 0;
  for (const chunk of chunks) { bytes.set(chunk, at); at += chunk.length; }
  return bytes;
}

/**
 * The seek-bar waveform of a file at a plain URL (blob:, same-origin stream, CDN), outside React: the bytes are fetched once
 * and handed to the shared waveformService, which decodes off the main thread and memoises per URL. `onState` hears the peaks
 * as they land (`null` until then, and forever when the file can't be decoded, has no audio or is too big -- the caller draws
 * its bar either way). Returns the unsubscribe; it also aborts a fetch still running for this listener.
 */
export function subscribeUrlWaveform(src: string, onState: (state: WaveformState | null) => void): () => void {
  const key = waveformCacheKey(`url:${src}`, 0);
  const publish = (state: WaveformState) => onState(state.peaks ? state : null);
  const off = subscribeWaveform(key, publish);
  const cached = peekWaveform(key);
  if (cached) publish(cached);
  if (cached?.final) return off;

  const abort = new AbortController();
  void (async () => {
    try {
      const res = await fetch(src, { signal: abort.signal });
      const length = Number(res.headers.get('content-length') ?? 0);
      if (!res.ok || length > WAVEFORM_MAX_SOURCE_BYTES) { markNoWaveform(key); return; }
      // A stream without a length is held to the same cap while it is read, not after it is all in memory.
      const bytes = await readCapped(res, WAVEFORM_MAX_SOURCE_BYTES);
      if (!bytes) { markNoWaveform(key); return; }
      feedWaveformBytes(key, bytes, 1, null);
    } catch {
      if (!abort.signal.aborted) markNoWaveform(key);
    }
  })();
  return () => { abort.abort(); off(); };
}
